// src/components/Landing/PrivacyPolicy.js
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="privacy-page">
      {/* Header Navigation */}
      <nav className="privacy-nav">
        <div className="privacy-nav-content">
          <Link to="/" className="privacy-logo">
            <img src="/logolandingpage.png" alt="DRE Pro Logo" />
          </Link>
          <Link to="/" className="privacy-back-btn">
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            <span>Voltar ao Site</span>
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="privacy-hero">
        <div className="privacy-hero-content">
          <h1>Política de Privacidade</h1>
          <p>Como protegemos e utilizamos seus dados na plataforma DRE Pro</p>
          <div className="privacy-meta">
            <span className="privacy-version">Versão 1.0</span>
            <span className="privacy-date">Última atualização: Janeiro de 2025</span>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="privacy-content">
        <div className="privacy-container">
          {/* Table of Contents */}
          <aside className="privacy-toc">
            <h3>Índice</h3>
            <ul>
              <li><a href="#introduction">1. Introdução</a></li>
              <li><a href="#data-controller">2. Controlador de Dados</a></li>
              <li><a href="#data-collection">3. Dados Coletados</a></li>
              <li><a href="#data-usage">4. Como Usamos seus Dados</a></li>
              <li><a href="#legal-basis">5. Base Legal</a></li>
              <li><a href="#data-sharing">6. Compartilhamento de Dados</a></li>
              <li><a href="#data-security">7. Segurança dos Dados</a></li>
              <li><a href="#data-retention">8. Retenção de Dados</a></li>
              <li><a href="#user-rights">9. Seus Direitos</a></li>
              <li><a href="#cookies">10. Cookies e Tecnologias</a></li>
              <li><a href="#international">11. Transferências Internacionais</a></li>
              <li><a href="#minors">12. Dados de Menores</a></li>
              <li><a href="#updates">13. Atualizações da Política</a></li>
              <li><a href="#contact">14. Contato</a></li>
            </ul>
          </aside>

          {/* Privacy Content */}
          <article className="privacy-article">
            <section id="introduction" className="privacy-section">
              <h2>1. INTRODUÇÃO</h2>
              <p>
                A <strong>DRE Pro</strong> está comprometida em proteger sua privacidade e dados pessoais. 
                Esta Política de Privacidade explica como coletamos, usamos, armazenamos e protegemos 
                suas informações em conformidade com a Lei Geral de Proteção de Dados (LGPD) e outras 
                legislações aplicáveis.
              </p>
              
              <div className="privacy-commitment">
                <h3>Nosso Compromisso</h3>
                <div className="privacy-commitment-grid">
                  <div className="privacy-commitment-item">
                    <h4>🔒 Transparência Total</h4>
                    <p>Informamos claramente como seus dados são utilizados</p>
                  </div>
                  <div className="privacy-commitment-item">
                    <h4>🛡️ Máxima Segurança</h4>
                    <p>Proteção avançada de dados financeiros sensíveis</p>
                  </div>
                  <div className="privacy-commitment-item">
                    <h4>⚖️ Conformidade Legal</h4>
                    <p>Aderência rigorosa à LGPD e regulamentações</p>
                  </div>
                  <div className="privacy-commitment-item">
                    <h4>👤 Controle do Usuário</h4>
                    <p>Você mantém controle total sobre seus dados</p>
                  </div>
                </div>
              </div>
            </section>

            <section id="data-controller" className="privacy-section">
              <h2>2. CONTROLADOR DE DADOS</h2>
              
              <div className="privacy-controller-info">
                <h3>Identificação do Controlador</h3>
                <div className="privacy-controller-details">
                  <div className="privacy-detail-item">
                    <h4>Razão Social:</h4>
                    <p>DRE Pro Soluções Financeiras LTDA</p>
                  </div>
                  <div className="privacy-detail-item">
                  </div>
                  <div className="privacy-detail-item">
                    <h4>Endereço:</h4>
                    <p>Curitiba, Paraná, Brasil</p>
                  </div>
                  <div className="privacy-detail-item">
                    <h4>DPO (Encarregado):</h4>
                    <p>suportedrepro@gmail.com</p>
                  </div>
                </div>
              </div>
            </section>

            <section id="data-collection" className="privacy-section">
              <h2>3. DADOS COLETADOS</h2>
              
              <h3>3.1 Dados de Identificação</h3>
              <div className="privacy-data-grid">
                <div className="privacy-data-category">
                  <h4>Dados Básicos</h4>
                  <ul>
                    <li>Nome completo</li>
                    <li>Email</li>
                    <li>Telefone</li>
                    <li>CPF/CNPJ</li>
                  </ul>
                </div>
                <div className="privacy-data-category">
                  <h4>Dados de Autenticação</h4>
                  <ul>
                    <li>Senha (criptografada)</li>
                    <li>Tokens de sessão</li>
                    <li>Histórico de login</li>
                    <li>Dispositivos utilizados</li>
                  </ul>
                </div>
              </div>

              <h3>3.2 Dados Financeiros</h3>
              <div className="privacy-financial-warning">
                <h4>⚠️ Dados Sensíveis Financeiros</h4>
                <p>Coletamos dados financeiros <strong>apenas</strong> quando você os fornece voluntariamente em contato com o suporte oficial:</p>
                <ul>
                  <li>Extratos bancários (formato OFX)</li>
                  <li>Histórico de transações</li>
                  <li>Categorias de gastos</li>
                  <li>Informações de receitas e despesas</li>
                  <li>Dados para cálculos tributários</li>
                </ul>
                <p><strong>Importante:</strong> NÃO coletamos senhas bancárias, dados de cartão ou informações de acesso a contas bancárias.</p>
              </div>

              <h3>3.3 Dados de Uso da Plataforma</h3>
              <div className="privacy-usage-data">
                <div className="privacy-usage-item">
                  <h4>Logs de Atividade</h4>
                  <p>Registros de uso da plataforma para melhoria da experiência</p>
                </div>
                <div className="privacy-usage-item">
                  <h4>Métricas de Performance</h4>
                  <p>Dados agregados e anonimizados sobre performance do sistema</p>
                </div>
                <div className="privacy-usage-item">
                  <h4>Preferências do Usuário</h4>
                  <p>Configurações personalizadas e preferências de uso</p>
                </div>
              </div>
            </section>

            <section id="data-usage" className="privacy-section">
              <h2>4. COMO USAMOS SEUS DADOS</h2>
              
              <div className="privacy-usage-purposes">
                <div className="privacy-purpose">
                  <h3>4.1 Prestação do Serviço</h3>
                  <div className="privacy-purpose-content">
                    <ul>
                      <li>Processar e categorizar transações financeiras</li>
                      <li>Gerar relatórios DRE personalizados</li>
                      <li>Calcular impostos e obrigações tributárias</li>
                      <li>Manter histórico de períodos fiscais</li>
                    </ul>
                  </div>
                </div>

                <div className="privacy-purpose">
                  <h3>4.2 Gestão da Conta</h3>
                  <div className="privacy-purpose-content">
                    <ul>
                      <li>Autenticar e autorizar acesso à plataforma</li>
                      <li>Gerenciar assinaturas e pagamentos</li>
                      <li>Fornecer suporte técnico</li>
                      <li>Comunicar atualizações importantes</li>
                    </ul>
                  </div>
                </div>

                <div className="privacy-purpose">
                  <h3>4.3 Melhorias e Desenvolvimento</h3>
                  <div className="privacy-purpose-content">
                    <ul>
                      <li>Aprimorar algoritmos de categorização</li>
                      <li>Desenvolver novos recursos</li>
                      <li>Análise de uso para otimização da UX</li>
                      <li>Pesquisa e desenvolvimento de IA</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            <section id="legal-basis" className="privacy-section">
              <h2>5. BASE LEGAL PARA PROCESSAMENTO</h2>
              
              <div className="privacy-legal-grid">
                <div className="privacy-legal-item">
                  <h3>Execução de Contrato</h3>
                  <p>Processamento necessário para fornecer os serviços contratados da plataforma DRE Pro.</p>
                  <div className="privacy-legal-examples">
                    <span>Exemplos:</span> Processamento de transações, geração de relatórios, autenticação
                  </div>
                </div>

                <div className="privacy-legal-item">
                  <h3>Consentimento</h3>
                  <p>Dados fornecidos voluntariamente com seu consentimento explícito.</p>
                  <div className="privacy-legal-examples">
                    <span>Exemplos:</span> Upload de extratos, preferências de comunicação
                  </div>
                </div>

                <div className="privacy-legal-item">
                  <h3>Interesse Legítimo</h3>
                  <p>Melhoria dos serviços e prevenção de fraudes.</p>
                  <div className="privacy-legal-examples">
                    <span>Exemplos:</span> Análises de segurança, otimização da plataforma
                  </div>
                </div>

                <div className="privacy-legal-item">
                  <h3>Cumprimento Legal</h3>
                  <p>Quando exigido por lei ou regulamentação.</p>
                  <div className="privacy-legal-examples">
                    <span>Exemplos:</span> Retenção de dados fiscais, relatórios regulatórios
                  </div>
                </div>
              </div>
            </section>

            <section id="data-sharing" className="privacy-section">
              <h2>6. COMPARTILHAMENTO DE DADOS</h2>
              
              <div className="privacy-sharing-policy">
                <div className="privacy-no-sharing">
                  <h3>🚫 Política de Não Compartilhamento</h3>
                  <p>
                    <strong>Seus dados financeiros NÃO são vendidos, alugados ou compartilhados 
                    com terceiros para fins comerciais.</strong>
                  </p>
                </div>

                <h3>6.1 Compartilhamentos Autorizados</h3>
                <div className="privacy-sharing-grid">
                  <div className="privacy-sharing-item">
                    <h4>Prestadores de Serviço</h4>
                    <p>Apenas quando necessário para operação da plataforma:</p>
                    <ul>
                      <li>Processamento de pagamentos (Stripe)</li>
                      <li>Infraestrutura de nuvem (Firebase/Google Cloud)</li>
                      <li>Serviços de autenticação</li>
                    </ul>
                  </div>

                  <div className="privacy-sharing-item">
                    <h4>Exigências Legais</h4>
                    <p>Quando obrigatório por lei:</p>
                    <ul>
                      <li>Ordens judiciais</li>
                      <li>Solicitações de autoridades fiscais</li>
                      <li>Investigações criminais</li>
                    </ul>
                  </div>

                  <div className="privacy-sharing-item">
                    <h4>Proteção de Direitos</h4>
                    <p>Para proteger nossos direitos ou dos usuários:</p>
                    <ul>
                      <li>Prevenção de fraudes</li>
                      <li>Segurança da plataforma</li>
                      <li>Cumprimento dos Termos de Uso</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            <section id="data-security" className="privacy-section">
              <h2>7. SEGURANÇA DOS DADOS</h2>
              
              <div className="privacy-security-measures">
                <h3>7.1 Medidas Técnicas</h3>
                <div className="privacy-security-grid">
                  <div className="privacy-security-item">
                    <h4>🔐 Criptografia</h4>
                    <ul>
                      <li>TLS 1.3 para dados em trânsito</li>
                      <li>AES-256 para dados em repouso</li>
                      <li>Senhas com hash bcrypt</li>
                    </ul>
                  </div>

                  <div className="privacy-security-item">
                    <h4>🛡️ Controle de Acesso</h4>
                    <ul>
                      <li>Autenticação multifator</li>
                      <li>Princípio do menor privilégio</li>
                      <li>Auditoria de acessos</li>
                    </ul>
                  </div>

                  <div className="privacy-security-item">
                    <h4>💾 Backup e Recuperação</h4>
                    <ul>
                      <li>Backups automáticos diários</li>
                      <li>Replicação em múltiplas regiões</li>
                      <li>Testes regulares de recuperação</li>
                    </ul>
                  </div>

                  <div className="privacy-security-item">
                    <h4>📊 Monitoramento</h4>
                    <ul>
                      <li>Detecção de anomalias 24/7</li>
                      <li>Logs de segurança</li>
                      <li>Alertas em tempo real</li>
                    </ul>
                  </div>
                </div>

                <h3>7.2 Medidas Organizacionais</h3>
                <div className="privacy-org-measures">
                  <div className="privacy-org-item">
                    <h4>Treinamento da Equipe</h4>
                    <p>Capacitação regular sobre proteção de dados e segurança</p>
                  </div>
                  <div className="privacy-org-item">
                    <h4>Políticas Internas</h4>
                    <p>Procedimentos rigorosos de manuseio de dados</p>
                  </div>
                  <div className="privacy-org-item">
                    <h4>Auditoria Externa</h4>
                    <p>Avaliações independentes de segurança</p>
                  </div>
                </div>
              </div>
            </section>

            <section id="data-retention" className="privacy-section">
              <h2>8. RETENÇÃO DE DADOS</h2>
              
              <div className="privacy-retention-policy">
                <h3>8.1 Períodos de Retenção</h3>
                <div className="privacy-retention-grid">
                  <div className="privacy-retention-item">
                    <h4>Dados de Conta Ativa</h4>
                    <p>Mantidos enquanto sua conta estiver ativa e até 30 dias após cancelamento</p>
                  </div>

                  <div className="privacy-retention-item">
                    <h4>Logs de Segurança</h4>
                    <p>Mantidos por 12 meses para fins de segurança</p>
                  </div>

                  <div className="privacy-retention-item">
                    <h4>Dados Anonimizados</h4>
                    <p>Podem ser retidos indefinidamente para pesquisa e melhoria</p>
                  </div>
                </div>

                <div className="privacy-retention-note">
                  <h3>⏰ Exclusão Automática</h3>
                  <p>
                    Após os períodos estabelecidos, os dados são automaticamente 
                    excluídos de nossos sistemas, exceto quando a retenção for 
                    exigida por lei.
                  </p>
                </div>
              </div>
            </section>

            <section id="user-rights" className="privacy-section">
              <h2>9. SEUS DIREITOS (LGPD)</h2>
              
              <div className="privacy-rights-grid">
                <div className="privacy-right">
                  <h3>📋 Acesso</h3>
                  <p>Solicitar informações sobre dados pessoais tratados</p>
                  <div className="privacy-right-action">
                    <span>Como exercer:</span> Contate nosso DPO
                  </div>
                </div>

                <div className="privacy-right">
                  <h3>✏️ Correção</h3>
                  <p>Corrigir dados incompletos, inexatos ou desatualizados</p>
                  <div className="privacy-right-action">
                    <span>Como exercer:</span> Via suporte
                  </div>
                </div>

                <div className="privacy-right">
                  <h3>🗑️ Exclusão</h3>
                  <p>Solicitar eliminação de dados pessoais</p>
                  <div className="privacy-right-action">
                    <span>Como exercer:</span> Cancelamento da conta ou solicitação ao DPO
                  </div>
                </div>

                <div className="privacy-right">
                  <h3>❌ Oposição</h3>
                  <p>Opor-se ao tratamento de dados pessoais</p>
                  <div className="privacy-right-action">
                    <span>Como exercer:</span> Solicitação formal ao DPO
                  </div>
                </div>


              </div>
              <div className="privacy-rights-contact">
                <h3>🏛️ Autoridade Nacional de Proteção de Dados (ANPD)</h3>
                <p>
                  Você também pode apresentar reclamações à ANPD caso considere 
                  que seus direitos não foram adequadamente atendidos.
                </p>
              </div>
            </section>

            <section id="cookies" className="privacy-section">
              <h2>10. COOKIES E TECNOLOGIAS SIMILARES</h2>
              
              <div className="privacy-cookies">
                <h3>10.1 Tipos de Cookies Utilizados</h3>
                <div className="privacy-cookies-grid">
                  <div className="privacy-cookie-type">
                    <h4>🔑 Cookies Essenciais</h4>
                    <p>Necessários para funcionamento da plataforma</p>
                    <ul>
                      <li>Sessão de usuário</li>
                      <li>Tokens de autenticação</li>
                      <li>Preferências de segurança</li>
                    </ul>
                  </div>

                  <div className="privacy-cookie-type">
                    <h4>⚙️ Cookies Funcionais</h4>
                    <p>Melhoram a experiência do usuário</p>
                    <ul>
                      <li>Preferências de interface</li>
                      <li>Configurações salvas</li>
                      <li>Idioma preferido</li>
                    </ul>
                  </div>

                  <div className="privacy-cookie-type">
                    <h4>📊 Cookies Analíticos</h4>
                    <p>Análise de uso da plataforma (anonimizados)</p>
                    <ul>
                      <li>Métricas de performance</li>
                      <li>Padrões de uso</li>
                      <li>Otimização da experiência</li>
                    </ul>
                  </div>
                </div>

                <div className="privacy-cookie-control">
                  <h3>🎛️ Controle de Cookies</h3>
                  <p>
                    Você pode gerenciar cookies através das configurações do seu navegador. 
                    Note que desabilitar cookies essenciais pode afetar o funcionamento da plataforma.
                  </p>
                </div>
              </div>
            </section>

            <section id="international" className="privacy-section">
              <h2>11. TRANSFERÊNCIAS INTERNACIONAIS</h2>
              
              <div className="privacy-international">
                <h3>11.1 Infraestrutura Global</h3>
                <p>
                  Utilizamos serviços de nuvem que podem processar dados em diferentes países, 
                  sempre com garantias adequadas de proteção:
                </p>

                <div className="privacy-international-providers">
                  <div className="privacy-provider">
                    <h4>Google Cloud Platform / Firebase</h4>
                    <ul>
                      <li>Certificações SOC 2, ISO 27001</li>
                      <li>Adequação às leis de proteção de dados</li>
                      <li>Contratos de proteção de dados (DPA)</li>
                    </ul>
                  </div>

                  <div className="privacy-provider">
                    <h4>Stripe (Processamento de Pagamentos)</h4>
                    <ul>
                      <li>Compliance PCI DSS Nível 1</li>
                      <li>Criptografia de ponta a ponta</li>
                      <li>Dados limitados ao mínimo necessário</li>
                    </ul>
                  </div>
                </div>

                <div className="privacy-safeguards">
                  <h3>🛡️ Garantias de Proteção</h3>
                  <ul>
                    <li>Cláusulas contratuais padrão</li>
                    <li>Certificações internacionais</li>
                    <li>Auditoria regular de fornecedores</li>
                    <li>Minimização de dados transferidos</li>
                  </ul>
                </div>
              </div>
            </section>

            <section id="minors" className="privacy-section">
              <h2>12. DADOS DE MENORES DE IDADE</h2>
              
              <div className="privacy-minors">
                <div className="privacy-minors-policy">
                  <h3>🔞 Política de Idade Mínima</h3>
                  <p>
                    A plataforma DRE Pro é destinada exclusivamente a pessoas com 
                    <strong> 18 anos ou mais</strong>. Não coletamos intencionalmente 
                    dados de menores de idade.
                  </p>
                </div>

                <div className="privacy-minors-action">
                  <h3>⚠️ Identificação de Dados de Menores</h3>
                  <p>
                    Caso identifiquemos que dados de menores foram coletados inadvertidamente, 
                    tomaremos medidas imediatas para:
                  </p>
                  <ul>
                    <li>Exclusão dos dados identificados</li>
                    <li>Suspensão da conta até regularização</li>
                    <li>Notificação aos responsáveis legais</li>
                  </ul>
                </div>
              </div>
            </section>

            <section id="updates" className="privacy-section">
              <h2>13. ATUALIZAÇÕES DA POLÍTICA</h2>
              
              <div className="privacy-updates">
                <h3>13.1 Processo de Atualização</h3>
                <div className="privacy-update-process">
                  <div className="privacy-update-step">
                    <h4>1. Notificação Prévia</h4>
                    <p>Usuários são notificados 30 dias antes de mudanças significativas</p>
                  </div>
                  <div className="privacy-update-step">
                    <h4>2. Destaque das Alterações</h4>
                    <p>Principais modificações são claramente identificadas</p>
                  </div>
                  <div className="privacy-update-step">
                    <h4>3. Período de Adaptação</h4>
                    <p>Prazo para revisar e manifestar oposição às mudanças</p>
                  </div>
                </div>

                <div className="privacy-version-control">
                  <h3>📋 Controle de Versões</h3>
                  <p>Mantemos histórico de todas as versões desta política para transparência.</p>
                  <div className="privacy-current-version">
                    <strong>Versão Atual:</strong> 1.0 - Janeiro de 2025
                  </div>
                </div>
              </div>
            </section>

            <section id="contact" className="privacy-section">
              <h2>14. CONTATO E DPO</h2>
              
              <div className="privacy-contact-final">
                <h3>Encarregado de Proteção de Dados (DPO)</h3>
                <div className="privacy-dpo-info">
                  <div className="privacy-contact-grid">
                    <div className="privacy-contact-item">
                      <h4>📧 Email do DPO</h4>
                      <p>suportedrepro@gmail.com</p>
                    </div>
                    <div className="privacy-contact-item">
                      <h4>📱 WhatsApp</h4>
                      <p>+55 41 3798-4531</p>
                    </div>
                    <div className="privacy-contact-item">
                      <h4>📮 Endereço Postal</h4>
                      <p>DRE Pro - DPO<br/>Curitiba, PR, Brasil</p>
                    </div>
                  </div>
                </div>

                <div className="privacy-contact-types">
                  <h3>📝 Tipos de Solicitação</h3>
                  <div className="privacy-request-types">
                    <div className="privacy-request-type">
                      <h4>Exercício de Direitos LGPD</h4>
                      <p>Acesso, correção, exclusão, portabilidade de dados</p>
                    </div>
                    <div className="privacy-request-type">
                      <h4>Incidentes de Segurança</h4>
                      <p>Relato de suspeitas de vazamento ou acesso não autorizado</p>
                    </div>
                    <div className="privacy-request-type">
                      <h4>Dúvidas sobre Privacidade</h4>
                      <p>Esclarecimentos sobre tratamento de dados</p>
                    </div>
                    <div className="privacy-request-type">
                      <h4>Reclamações</h4>
                      <p>Questões relacionadas ao cumprimento desta política</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <div className="privacy-footer-text">
              <p>
                <strong>
                  Esta Política de Privacidade foi elaborada em conformidade com a LGPD 
                  e reflete nosso compromisso com a proteção de seus dados pessoais.
                </strong>
              </p>
              <p className="privacy-version-final">Versão 1.0 - Janeiro de 2025</p>
            </div>
          </article>
        </div>
      </main>

      {/* Footer */}
      <footer className="privacy-footer">
        <div className="privacy-footer-content">
          <Link to="/" className="privacy-footer-logo">
            <img src="/logolandingpage.png" alt="DRE Pro Logo" />
          </Link>
          <p>&copy; {new Date().getFullYear()} DRE Pro. Todos os direitos reservados.</p>
          <Link to="/" className="privacy-footer-back">
            Voltar ao Site Principal
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;